/**
 * Community + safe-space vocabulary and limits, shared by the server (Zod,
 * repositories, the migration's CHECK lists) and the client (composer, filters,
 * settings). One definition, so a topic or a badge can never exist on one side
 * only. The values mirror the CHECK constraints in migrations/0021_community.sql
 * (topics: as widened by migrations/0026_community_topics.sql).
 */
import type { Market } from "./market.js";

/* ---------- topics (= the community's boards) ---------- */

/**
 * Topic labels double as display text. Every topic is a board ("like
 * subreddits", Oct 22): /community lists them, and a post appears on every
 * board it is tagged with. A post's FIRST topic is the board it was posted to.
 * "Caring for aging parents" and "Grief & loss" were added on Oct 22.
 */
export const COMMUNITY_TOPICS = [
  "Newborn life",
  "Toddlers",
  "School",
  "Health",
  "Rainy season",
  "Working parents",
  "Caring for aging parents",
  "Grief & loss",
  "Mental health",
  "Wins",
] as const;
export type CommunityTopic = (typeof COMMUNITY_TOPICS)[number];

/**
 * One warm line per board: the subtitle on the boards list and the board's own
 * page. Here (not in the client) so the server's boards endpoint, the demo
 * mock and the UI all say the same thing.
 */
export const TOPIC_DESCRIPTIONS: Record<CommunityTopic, string> = {
  "Newborn life": "Night feeds, first smiles and the fourth trimester, together.",
  Toddlers: "Big feelings, first words and tiny negotiators.",
  School: "Drop-offs, homework, friendships and growing independence.",
  Health: "Coughs, check-ups and everyday care. Experience, not diagnoses.",
  "Rainy season": "Typhoon prep, flooded streets and rainy-day ideas.",
  "Working parents": "Juggling work and family, from pumping breaks to daycare goodbyes.",
  "Caring for aging parents": "For the sandwich years: caring for your kids and your own parents.",
  "Grief & loss": "A gentle place to talk about loss of any kind, at your own pace.",
  "Mental health": "The heavy days and the hopeful ones. You're not alone here.",
  Wins: "Big or tiny, every win gets cheered here.",
};

/**
 * Stable URL slugs for the boards (/community/board/<slug>). Spelled out, not
 * derived, so renaming a label never breaks a saved link.
 */
export const TOPIC_SLUGS: Record<CommunityTopic, string> = {
  "Newborn life": "newborn-life",
  Toddlers: "toddlers",
  School: "school",
  Health: "health",
  "Rainy season": "rainy-season",
  "Working parents": "working-parents",
  "Caring for aging parents": "caring-for-aging-parents",
  "Grief & loss": "grief-and-loss",
  "Mental health": "mental-health",
  Wins: "wins",
};

export function topicSlug(topic: CommunityTopic): string {
  return TOPIC_SLUGS[topic];
}

/** The topic behind a board slug, or null for an unknown one. */
export function topicFromSlug(slug: string): CommunityTopic | null {
  return COMMUNITY_TOPICS.find((t) => TOPIC_SLUGS[t] === slug) ?? null;
}

/**
 * The "Heavier topics" boards (Ford's "separate place for heavy or triggering
 * stories"): grouped apart on the boards list, opened through a short content
 * note, Anonymous pre-selected in the composer, the crisis-support card on the
 * board, and never any sponsored or partner content.
 */
export const HEAVIER_TOPICS: readonly CommunityTopic[] = ["Mental health", "Grief & loss"];

export function isHeavierTopic(topic: CommunityTopic): boolean {
  return HEAVIER_TOPICS.includes(topic);
}

/**
 * The boards list's order: the regular boards in topic order, then the
 * heavier ones in HEAVIER_TOPICS order (Mental health, Grief & loss). One
 * rule for the server and the demo mock.
 */
export function orderBoards<T extends { topic: CommunityTopic }>(boards: readonly T[]): T[] {
  return [
    ...boards.filter((b) => !isHeavierTopic(b.topic)),
    ...HEAVIER_TOPICS.flatMap((t) => boards.filter((b) => b.topic === t)),
  ];
}

/**
 * The topics each market offers. "Rainy season" is Leena's List Philippines
 * only: the main market shows no seasonal content anywhere (BRIEF §5.7). A
 * post already tagged with it still shows its tag; the main-market composer
 * and filters just don't offer it, and the server refuses it there.
 */
export const TOPICS_BY_MARKET: Record<Market, readonly CommunityTopic[]> = {
  main: COMMUNITY_TOPICS.filter((t) => t !== "Rainy season"),
  ph: COMMUNITY_TOPICS,
};

export function topicsForMarket(market: Market): readonly CommunityTopic[] {
  return TOPICS_BY_MARKET[market];
}

/** Topics that pre-select Anonymous (when that setting is on) and never sit next to ads. */
export const SENSITIVE_TOPICS: readonly CommunityTopic[] = ["Mental health", "Health", "Grief & loss"];

/* ---------- posts, reactions, reports ---------- */

/** Share · Ask for advice · Celebrate a win. */
export const POST_TYPES = ["share", "advice", "win"] as const;
export type PostType = (typeof POST_TYPES)[number];

/** Everyone (named) · My circles (named, members only) · Anonymous (name hidden). */
export const POST_AUDIENCES = ["everyone", "circles", "anonymous"] as const;
export type PostAudience = (typeof POST_AUDIENCES)[number];

/** Gentle reactions instead of likes: Sending hugs · Been there · Helpful. */
export const REACTION_KINDS = ["hugs", "beenThere", "helpful"] as const;
export type ReactionKind = (typeof REACTION_KINDS)[number];

export const REPORT_REASONS = [
  "unkind",
  "unsafeAdvice",
  "childPrivacy",
  "spam",
  "atRisk",
  "other",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

/**
 * Distinct reporters it takes to hide a post or reply for EVERYONE until a
 * moderator looks. One report already hides it for the reporter.
 */
export const REPORT_AUTO_HIDE_THRESHOLD = 3;

/* ---------- badges ---------- */

export const COMMUNITY_BADGES = [
  "mentorParent",
  "verifiedNurse",
  "verifiedDoctor",
  "verifiedTeacher",
  "foundingMember",
  "storySharer",
  "kindNeighbor",
  "preparedParent",
] as const;
export type CommunityBadgeId = (typeof COMMUNITY_BADGES)[number];

/** Credential-checked badges: granted and revoked by platform admins only. */
export const VERIFIED_BADGES = ["verifiedNurse", "verifiedDoctor", "verifiedTeacher"] as const;
export type VerifiedBadgeId = (typeof VERIFIED_BADGES)[number];

/**
 * Accounts created before this instant carry the Founding Member badge. It is
 * derived on read from users.created_at, never stored, so moving the date
 * moves every badge with it. 2026-12-31 for now (the launch date is Ford's
 * call); midnight UTC.
 */
export const FOUNDING_MEMBER_CUTOFF: string = "2026-12-31T00:00:00.000Z";

export function isFoundingMember(accountCreatedAt: Date | string): boolean {
  const created = typeof accountCreatedAt === "string" ? new Date(accountCreatedAt) : accountCreatedAt;
  return created.getTime() < Date.parse(FOUNDING_MEMBER_CUTOFF);
}

/** Display order: verified credentials first, then earned badges. */
export const BADGE_ORDER: readonly CommunityBadgeId[] = [
  "verifiedNurse",
  "verifiedDoctor",
  "verifiedTeacher",
  "mentorParent",
  "foundingMember",
  "storySharer",
  "kindNeighbor",
  "preparedParent",
];

/**
 * A member's badges: their live granted badges (user_badges) plus the derived
 * Founding Member, in display order. Unknown strings are dropped.
 */
export function memberBadges(grants: readonly string[], accountCreatedAt: Date | string): CommunityBadgeId[] {
  const set = new Set<string>(grants);
  if (isFoundingMember(accountCreatedAt)) set.add("foundingMember");
  return BADGE_ORDER.filter((b) => set.has(b));
}

/* ---------- circles ---------- */

/** The six pastel accents (client/src/lib/accents.ts), as plain strings for the server. */
export const CIRCLE_ACCENTS = ["sky", "rose", "amber", "emerald", "violet", "indigo"] as const;
export type CircleAccent = (typeof CIRCLE_ACCENTS)[number];

/**
 * age  — by the baby's or child's age; offered in every market.
 * city — by city; Leena's List Philippines only for now (the six PH_REGIONS).
 */
export const CIRCLE_KINDS = ["age", "city"] as const;
export type CircleKind = (typeof CIRCLE_KINDS)[number];

/** Which market lists a circle: one of them, or both. */
export const CIRCLE_MARKETS = ["main", "ph", "all"] as const;
export type CircleMarket = (typeof CIRCLE_MARKETS)[number];

/* ---------- limits (Zod and the migration's CHECKs agree) ---------- */

export const COMMUNITY_LIMITS = {
  postChars: 2000,
  commentChars: 1000,
  topicsPerPost: 3,
  /** users.community_display_name CHECK (migration 0018). */
  displayNameChars: 40,
  reportNoteChars: 500,
  mutedPeople: 200,
  moderationCheckChars: 5000,
  feedPageDefault: 8,
  feedPageMax: 20,
} as const;
